import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { ValidationError } from "../../app/slices/ValidationSlice";
import {
  getInteractionFromFactory,
  InteractionFactoryEntity,
  ReconcileContext,
} from "../../features/interaction_factory/InteractionFactory";
import SequencePreview from "../../features/sequence/SequencePreview";
import { Actor } from "../Actor";
import { Prop } from "../Prop";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";
import SequenceNodePreview from "../../features/sequence/node_previews/SequenceNodePreview";
import { SWITCH_DEFAULT_HANDLE_ID } from "./Switch";
import { TRIGGER_PARALLEL_OUTPUT_HANDLE_ID } from "./Trigger";
import {
  copySequenceInteraction,
  createDefaultTake3DSequenceInteraction,
  createDefaultSequenceInteraction,
  ensureTake3DSequenceDefaults,
  ISequence,
} from "./SequenceModel";

export type {
  ISequence,
  SequenceCommentZone,
  SequenceNodeData,
  SequenceTextbox,
} from "./SequenceModel";
export {
  copySequenceInteraction,
  createDefaultTake3DSequenceInteraction,
  createDefaultSequenceInteraction,
  ensureTake3DSequenceDefaults,
} from "./SequenceModel";

export const sequenceInteraction = (): InteractionFactoryEntity => {
  const view = <SequencePreview />;

  const create = (getKeys: (amount: number) => string[]): ISequence => {
    return createDefaultSequenceInteraction();
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction,
  ): ISequence => {
    const sequence = interaction as ISequence;
    if (!sequence.nodes || !sequence.edges) {
      return sequence;
    }

    const switchNodeIds = new Set(
      sequence.nodes
        .filter(
          (node) => node.data?.interaction?.type === InteractionType.Switch,
        )
        .map((node) => node.id),
    );
    const triggerNodeIds = new Set(
      sequence.nodes
        .filter(
          (node) => node.data?.interaction?.type === InteractionType.Trigger,
        )
        .map((node) => node.id),
    );

    if (switchNodeIds.size === 0 && triggerNodeIds.size === 0) {
      return sequence;
    }

    let edgesChanged = false;
    const migratedEdges = sequence.edges.map((edge) => {
      if (
        switchNodeIds.has(edge.source) &&
        (edge.sourceHandle === null || edge.sourceHandle === undefined)
      ) {
        edgesChanged = true;
        return {
          ...edge,
          sourceHandle: SWITCH_DEFAULT_HANDLE_ID,
        };
      }
      if (
        triggerNodeIds.has(edge.source) &&
        (edge.sourceHandle === null || edge.sourceHandle === undefined)
      ) {
        edgesChanged = true;
        return {
          ...edge,
          sourceHandle: TRIGGER_PARALLEL_OUTPUT_HANDLE_ID,
        };
      }
      return edge;
    });

    if (!edgesChanged) {
      return sequence;
    }

    return {
      ...sequence,
      edges: migratedEdges,
    };
  };

  const reconcile = (
    interaction: Interaction,
    context: ReconcileContext,
  ): ISequence => {
    const sequence = interaction as ISequence;
    if (!sequence.nodes?.length) return sequence;

    let changed = false;
    const nodes = sequence.nodes.map((node) => {
      const nested = node.data?.interaction;
      if (!nested) return node;
      const factoryEntity = getInteractionFromFactory(nested.type);
      const reconciled = factoryEntity?.Reconcile
        ? factoryEntity.Reconcile(nested, context)
        : nested;
      if (reconciled === nested) return node;
      changed = true;
      return { ...node, data: { ...node.data, interaction: reconciled } };
    });

    return changed ? { ...sequence, nodes } : sequence;
  };

  // TODO: validation functionality
  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
  ): string[] => {
    return [];
  };

  // TODO: Filter functionality
  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string,
    ) => SearchResultData[],
  ): SearchResultData[] => {
    return [];
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord,
  ): (ValidationError | string)[] => {
    const sequence = interaction as ISequence;
    const errors: (ValidationError | string)[] = [];

    // Check if sequence has nodes
    if (!sequence.nodes || sequence.nodes.length === 0) {
      //errors.push({ message: "Sequence must have at least one node" });
      return errors;
    }

    // Check if all edges reference valid nodes
    if (sequence.edges && sequence.edges.length > 0) {
      const nodeIds = new Set(sequence.nodes.map((node) => node.id));

      for (const edge of sequence.edges) {
        if (!nodeIds.has(edge.source)) {
          errors.push({
            message: `Edge references non-existent source node: ${edge.source}`,
          });
        }
        if (!nodeIds.has(edge.target)) {
          errors.push({
            message: `Edge references non-existent target node: ${edge.target}`,
          });
        }
      }
    }

    // Validate each node's interaction (if present)
    for (const node of sequence.nodes) {
      if (node.data?.interaction) {
        const nodeInteraction = node.data.interaction;
        const factoryEntity = getInteractionFromFactory(nodeInteraction.type);

        if (factoryEntity?.ValidateErrors) {
          const nodeErrors = factoryEntity.ValidateErrors(
            nodeInteraction,
            parentTake,
            currentTake,
            actorPresets,
            allPropPresets,
            localizedStringRecords,
          );
          if (nodeErrors.length > 0) {
            const displayName =
              nodeInteraction.name || node.data.name || node.id;
            const errorMessages = nodeErrors.map(
              (e: ValidationError | string) =>
                typeof e === "string" ? e : e.message,
            );
            errors.push({
              message: `Node "${displayName}": ${errorMessages.join(", ")}`,
              nodeId: node.id, // Use the React Flow node ID, not the interaction ID
            });
          }
        }
      }
    }

    return errors;
  };

  return {
    View: view,
    Create: create,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <SequenceNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: "Sequence",
    Migrate: migrate,
    Copy: copySequenceInteraction,
    Reconcile: reconcile,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
